import {
  BULK_EDIT_RECORD_TYPES,
  BULK_EDIT_SELECTION_SOURCES,
  type BulkEditRecordType,
  type BulkEditSelectionSource,
} from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { type BulkEditFlowState } from "./flow-state";

type ReportQueryFilter = components["schemas"]["ReportQueryFilter"];

/** The most filters one selection may carry: the selection input's `maxItems`. */
export const MAX_SELECTION_FILTERS = 10;

/** A step of the new-edit flow: client state the URL carries, never a wire value. */
export type BulkEditFlowStep = "records" | "change" | "review";

/** The new-edit flow's three steps, in order. */
export const BULK_EDIT_FLOW_STEPS: readonly BulkEditFlowStep[] = ["records", "change", "review"];

/**
 * The flow's URL state. The step and the selection live here so browser Back
 * walks the steps and a reload restores the selection; the change settings
 * live in the reducer, and at review `draft` names the staged run they can be
 * rebuilt from. `from_run` is the failed run whose note and reason Run again
 * prefills.
 * An absent `step` is `records`.
 */
export interface BulkEditFlowSearch {
  step?: BulkEditFlowStep;
  record_type?: BulkEditRecordType;
  source?: BulkEditSelectionSource;
  filters?: ReportQueryFilter[];
  report_id?: string;
  draft?: string;
  from_run?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function memberOf<T extends string>(values: readonly T[], raw: unknown): T | undefined {
  return values.find((value) => value === raw);
}

function parseUuid(raw: unknown): string | undefined {
  return typeof raw === "string" && UUID_RE.test(raw) ? raw : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A filter list the URL may carry: at most `MAX_SELECTION_FILTERS` objects,
 * each with a string `field` and `op`. One bad item drops the whole list
 * rather than selecting with part of it; whether a field, operator or value is
 * legal is the server's to say.
 */
function parseFilters(raw: unknown): ReportQueryFilter[] | undefined {
  if (!Array.isArray(raw) || raw.length > MAX_SELECTION_FILTERS) return undefined;
  const filters: ReportQueryFilter[] = [];
  for (const item of raw) {
    if (!isRecord(item) || typeof item.field !== "string" || typeof item.op !== "string") {
      return undefined;
    }
    // The operator is held to the wire's union by the server, which refuses an
    // unknown one as an invalid selection; the URL only proves it is a string.
    const op = item.op as ReportQueryFilter["op"];
    filters.push(
      item.value === undefined
        ? { field: item.field, op }
        : { field: item.field, op, value: item.value },
    );
  }
  return filters;
}

/**
 * The flow's search as the router hands it over, every member held to its
 * shape: a step, record type or source outside its vocabulary, an id that
 * cannot be a UUID, or a malformed filter list is dropped rather than sent.
 */
export function parseBulkEditFlowSearch(raw: Record<string, unknown>): BulkEditFlowSearch {
  const search: BulkEditFlowSearch = {};
  const step = memberOf(BULK_EDIT_FLOW_STEPS, raw.step);
  if (step !== undefined) search.step = step;
  const recordType = memberOf(BULK_EDIT_RECORD_TYPES, raw.record_type);
  if (recordType !== undefined) search.record_type = recordType;
  const source = memberOf(BULK_EDIT_SELECTION_SOURCES, raw.source);
  if (source !== undefined) search.source = source;
  const filters = parseFilters(raw.filters);
  if (filters !== undefined) search.filters = filters;
  const reportId = parseUuid(raw.report_id);
  if (reportId !== undefined) search.report_id = reportId;
  const draft = parseUuid(raw.draft);
  if (draft !== undefined) search.draft = draft;
  const fromRun = parseUuid(raw.from_run);
  if (fromRun !== undefined) search.from_run = fromRun;
  return search;
}

/**
 * The URL a flow state writes: the step, the record type and source, the
 * source's own member (a non-empty filter list, or the saved report), and on
 * the review step alone the draft and, while the note came from a run, that
 * run.
 */
export function flowSearchOf(state: BulkEditFlowState): BulkEditFlowSearch {
  const search: BulkEditFlowSearch = { step: state.step };
  if (state.recordType !== null) {
    search.record_type = state.recordType;
    search.source = state.source;
    const filters = state.filterState.doc.filters ?? [];
    if (state.source === "filters" && filters.length > 0) search.filters = [...filters];
    if (state.source === "saved_report" && state.reportId !== null) {
      search.report_id = state.reportId;
    }
  }
  if (state.step === "review") {
    if (state.draftId !== null) search.draft = state.draftId;
    if (state.noteFromRunId !== null) search.from_run = state.noteFromRunId;
  }
  return search;
}
